/**
 * URL-safe slug from store display name (e.g. "Luxe" → `luxe`).
 * @param {string|null|undefined} storeName
 * @returns {string}
 */
export function slugifyStoreName(storeName) {
  if (!storeName) {
    return "";
  }
  return String(storeName)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
}

/**
 * Slug for a store object (`storeName` or `name`).
 * @param {object|null|undefined} store
 * @returns {string|null}
 */
export function getStorePathSlug(store) {
  if (!store) {
    return null;
  }
  const slug = slugifyStoreName(store.storeName || store.name);
  return slug || null;
}

/** Fixed msite path prefix for brand store home (`/store/luxe`). */
export const MOBILE_STORE_PATH_PREFIX = "store";

/**
 * Builds the msite store path segment for a slug (e.g. `luxe` → `/store/luxe`).
 * @param {string|null|undefined} slug
 * @returns {string}
 */
export function buildMobileStorePath(slug) {
  if (!slug) {
    return `/${MOBILE_STORE_PATH_PREFIX}`;
  }
  return `/${MOBILE_STORE_PATH_PREFIX}/${encodeURIComponent(slug)}`;
}

/**
 * @param {string} storeId
 * @returns {string}
 */
function normalizeStoreId(storeId) {
  return String(storeId).toLowerCase();
}

/**
 * @param {Array<object>|undefined|null} stores
 * @param {string|null|undefined} storeId
 * @returns {object|undefined}
 */
export function findStoreByStoreId(stores, storeId) {
  if (!Array.isArray(stores) || !storeId) {
    return undefined;
  }
  const normalized = normalizeStoreId(storeId);
  return stores.find((store) => normalizeStoreId(store.storeId) === normalized);
}

/**
 * Finds a store whose slugified `storeName` matches the path segment (e.g. `global`).
 * @param {Array<object>|undefined|null} stores
 * @param {string|null|undefined} pathSlug
 * @returns {object|undefined}
 */
export function findStoreByPathSlug(stores, pathSlug) {
  if (!Array.isArray(stores) || !pathSlug) {
    return undefined;
  }
  const normalizedSlug = normalizeStoreId(pathSlug);
  return stores.find((store) => {
    const slug = getStorePathSlug(store);
    return slug && normalizeStoreId(slug) === normalizedSlug;
  });
}

/**
 * Display name used by the masthead gateway (`storeName` query).
 * @param {object|null|undefined} store
 * @returns {string|null}
 */
export function getStoreDisplayName(store) {
  if (!store) {
    return null;
  }
  const name = store.storeName || store.name;
  return name ? String(name) : null;
}

/**
 * Resolves masthead display `storeName` from a path slug using a store list.
 * @param {Array<object>|undefined|null} stores
 * @param {string|null|undefined} storePathSlug
 * @returns {string|null}
 */
export function resolveMastheadStoreName(stores, storePathSlug) {
  return getStoreDisplayName(findStoreByPathSlug(stores, storePathSlug));
}

/**
 * First store in the list is the home store (`/`).
 *
 * @param {Array<object>|undefined|null} stores
 * @param {Array<object>|undefined|null} [canonicalStores]
 * @returns {object|undefined}
 */
export function getHomeStore(stores, canonicalStores) {
  if (Array.isArray(canonicalStores) && canonicalStores.length > 0) {
    return canonicalStores[0];
  }
  if (Array.isArray(stores) && stores.length > 0) {
    return stores[0];
  }
  return undefined;
}

/**
 * Default store for mobile home — first store in the list (e.g. FNP at `/`).
 * @param {Array<object>|undefined|null} stores
 * @returns {object|undefined}
 */
export function getDefaultStore(stores) {
  return getHomeStore(stores);
}

/**
 * Whether `store` is the home brand — always the first store, served at `/`.
 * @param {object|null|undefined} store
 * @param {Array<object>|undefined|null} stores
 * @param {Array<object>|undefined|null} [canonicalStores]
 * @returns {boolean}
 */
export function isDefaultStore(store, stores, canonicalStores) {
  const homeStore = getHomeStore(stores, canonicalStores);
  if (!store?.storeId || !homeStore?.storeId) {
    return false;
  }
  return normalizeStoreId(store.storeId) === normalizeStoreId(homeStore.storeId);
}

/**
 * Redirect target preserving non-routing query params.
 * @param {string} storePageUrl - From `getStorePageUrl`
 * @param {URLSearchParams|Record<string, string|string[]|undefined>|null|undefined} searchParams
 * @returns {string}
 */
export function buildStoreHomeRedirectUrl(storePageUrl, searchParams) {
  if (!storePageUrl || storePageUrl === "/") {
    return storePageUrl;
  }
  const url = new URL(storePageUrl, "http://localhost");
  if (!searchParams) {
    return `${url.pathname}${url.search}`;
  }
  if (typeof searchParams.forEach === "function") {
    searchParams.forEach((value, key) => {
      if (key !== "storeId") {
        url.searchParams.set(key, value);
      }
    });
  } else {
    Object.entries(searchParams).forEach(([key, value]) => {
      if (key === "storeId") {
        return;
      }
      const resolved = Array.isArray(value) ? value[0] : value;
      if (resolved != null && resolved !== "") {
        url.searchParams.set(key, String(resolved));
      }
    });
  }
  return `${url.pathname}${url.search}`;
}

/**
 * Sets `isSelected` on the matching store for SSR.
 * @param {Array<object>|undefined|null} stores
 * @param {string|null|undefined} storeId
 * @returns {Array<object>}
 */
export function markSelectedStore(stores, storeId) {
  if (!storeId || !Array.isArray(stores)) {
    return stores ?? [];
  }
  const normalized = normalizeStoreId(storeId);
  return stores.map((store) => ({
    ...store,
    isSelected: normalizeStoreId(store.storeId) === normalized,
  }));
}

/**
 * PLP slugs end with `-lp`; brand store slugs must not.
 * @param {string|null|undefined} segment
 * @returns {boolean}
 */
export function isPlpPathSegment(segment) {
  if (!segment) {
    return false;
  }
  return String(segment).toLowerCase().includes("-lp");
}

/**
 * Msite brand store home path (e.g. `/store/luxe`).
 * @param {string|null|undefined} pathname
 * @returns {boolean}
 */
export function isStoreHomePathname(pathname) {
  if (!pathname?.startsWith(`/${MOBILE_STORE_PATH_PREFIX}/`)) {
    return false;
  }
  const slug = pathname.split("/").filter(Boolean)[1];
  return Boolean(slug) && !isPlpPathSegment(slug);
}

/**
 * True for default home (`/`) or msite brand store home (`/store/[slug]`).
 * @param {string|null|undefined} pathname
 * @returns {boolean}
 */
export function isHomePathname(pathname) {
  return pathname === "/" || isStoreHomePathname(pathname);
}

/**
 * Public msite store URL: `/` for the default store, otherwise `/store/luxe`.
 * @param {object|null|undefined} store
 * @param {Array<object>|undefined|null} [stores] - When provided, default store maps to `/`
 * @param {Array<object>|undefined|null} [canonicalStores] - Unscoped store list for default detection
 * @returns {string}
 */
export function getStorePageUrl(store, stores, canonicalStores) {
  if (stores && isDefaultStore(store, stores, canonicalStores)) {
    return "/";
  }
  const slug = getStorePathSlug(store);
  if (!slug) {
    return "/";
  }
  return buildMobileStorePath(slug);
}

/**
 * Builds the client URL for a brand store soft-navigation.
 * Preserves non-routing query params; strips legacy `storeId`.
 * @param {string} storePageUrl - From `getStorePageUrl` (`/` or `/store/luxe`)
 * @param {string} currentHref - `window.location.href`
 * @returns {string|null}
 */
export function buildBrandStoreClientUrl(storePageUrl, currentHref) {
  if (!storePageUrl || !currentHref) {
    return null;
  }

  const current = new URL(currentHref);
  const next = new URL(storePageUrl, current.origin);

  current.searchParams.forEach((value, key) => {
    if (key !== "storeId") {
      next.searchParams.set(key, value);
    }
  });
  next.searchParams.delete("storeId");

  return `${next.pathname}${next.search}`;
}

/**
 * Soft-replaces the browser URL to a brand store path without a Next.js navigation.
 * @param {string} storePageUrl - From `getStorePageUrl` (`/` or `/store/luxe`)
 * @returns {void}
 */
export function replaceBrandStoreUrl(storePageUrl) {
  if (typeof window === "undefined" || !storePageUrl) {
    return;
  }

  const nextPath = buildBrandStoreClientUrl(storePageUrl, window.location.href);
  if (!nextPath) {
    return;
  }

  const currentPath = `${window.location.pathname}${window.location.search}`;
  if (nextPath !== currentPath) {
    window.history.replaceState(null, "", nextPath);
  }
}

/**
 * Canonical URL for a brand store: `/` (default) or `/store/[slug]`.
 * @param {string} pathSlug
 * @param {string} baseUrl
 * @param {Array<object>|undefined|null} [stores]
 * @param {Array<object>|undefined|null} [canonicalStores]
 * @returns {string|null}
 */
export function buildStoreCanonicalUrl(pathSlug, baseUrl, stores, canonicalStores) {
  if (!pathSlug || !baseUrl) {
    return null;
  }
  const normalizedBase = baseUrl.replace(/\/$/, "");
  const matched = findStoreByPathSlug(stores, pathSlug);
  if (stores && matched && isDefaultStore(matched, stores, canonicalStores)) {
    return `${normalizedBase}/`;
  }
  return `${normalizedBase}${buildMobileStorePath(pathSlug)}`;
}

/**
 * Resolves a store for `/store/[slug]` by path slug (storeName).
 * @param {Array<object>|undefined|null} stores
 * @param {string|null|undefined} pathSlug
 * @returns {object|undefined}
 */
export function resolveStoreForStoreRoute(stores, pathSlug) {
  return findStoreByPathSlug(stores, pathSlug);
}

/**
 * Prefer scoped masthead stores; fall back to the unscoped canonical list.
 *
 * @param {Array<object>|undefined|null} stores
 * @param {Array<object>|undefined|null} canonicalStores
 * @returns {Array<object>}
 */
export function getEffectiveStoreList(stores, canonicalStores) {
  if (Array.isArray(stores) && stores.length > 0) {
    return stores;
  }

  if (Array.isArray(canonicalStores) && canonicalStores.length > 0) {
    return canonicalStores;
  }

  return [];
}

/**
 * Resolves a store for `/store/[slug]` using the path slug.
 *
 * @param {Array<object>|undefined|null} stores
 * @param {Array<object>|undefined|null} canonicalStores
 * @param {string|null|undefined} storePathSlug
 * @returns {object|undefined}
 */
export function resolveStoreForHomepage(stores, canonicalStores, storePathSlug) {
  const effectiveStores = getEffectiveStoreList(stores, canonicalStores);
  const lookupStores = effectiveStores.length > 0 ? effectiveStores : (canonicalStores ?? []);
  return findStoreByPathSlug(lookupStores, storePathSlug);
}
